import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import LoginScreenImage from "../assets/auth/LoginScreen.png";
import BrianyCareLogo from "../assets/auth/BrainyCare-Logo.jpg";
import BrainyMedLogo from "../assets/auth/BrainyMedLogo.svg";

import api from "../api/api";
import AppHeader from "../header/header";
import dayjs from "dayjs";
// Import FontAwesome components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faTimes } from "@fortawesome/free-solid-svg-icons";
import ToastMessage from "../utils/mui/righttoptoast";
import { fetchUserConfig } from "../reduxtoolkit/userconfigslice";
import { useDispatch } from "react-redux";
import { setPracticeSession } from "../utils/practice";

export const LogIn = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [practiceCode, setPracticeCode] = useState(
    localStorage.getItem("practiceid") || ""
  );
  const [error, setError] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal visibility state
  const [severity, setSeverity] = useState(""); // Severity dropdown state
  const [details, setDetails] = useState(""); // Text area state
  const [email, setEmail] = useState(""); // Email state
  const [subject, setSubject] = useState(""); // Subject state
  const [modalError, setModalError] = useState(""); // Modal error message state
  const [name, setName] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const params = new URLSearchParams();
  const handleToastClose = () => {
    setOpenToast(false);
  };
  // Persist the session and re-enter the app with a FULL page reload —
  // module-scope window.getPracticeId() captures re-evaluate with the
  // fresh practice id (runtime tenancy; see utils/practice.js).
  const completeLogin = (access_token, data) => {
    setPracticeSession(practiceCode, data?.[0]?.departmentid);
    localStorage.setItem("auth", access_token);
    localStorage.setItem("user", data?.[0]?.username);
    localStorage.setItem("firstname", data?.[0]?.firstname);
    localStorage.setItem("lastname", data?.[0]?.lastname);
    localStorage.setItem("role", data?.[0]?.role);
    window.location.replace("/landing");
  };
  const handleLogin = async () => {
    if (!/^\d+$/.test(practiceCode.trim())) {
      setError("Please enter your numeric practice code.");
      return;
    }
    params.append("username", username);
    params.append("password", password);
    params.append("client_id", practiceCode.trim());
    if (!username.length > 0 || !password.length > 0) {
      setError("Please enter username and password.");
      return;
    }

    try {
      const tokenresp = await api.post("/auth/token", params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (tokenresp.status === 200) {
        const { auth, access_token, data, id } = tokenresp;

        if (access_token) {
          const lastPassword = data[0]?.last_password;

          if (lastPassword) {
            const lastPasswordDate = dayjs(lastPassword);
            const today = dayjs();
            const diffDays = today.diff(lastPasswordDate, "day");
            const daysLeft = 85 - diffDays;

            if (daysLeft <= 5) {
              setError(
                `Your password will expire in ${daysLeft} day${
                  daysLeft === 1 ? "" : "s"
                }.`
              );
              setTimeout(() => {
                setError("");
                completeLogin(access_token, data);
              }, 2000);
            } else {
              setError("");
              completeLogin(access_token, data);
            }
          } else {
            // Fallback: no last_password field
            completeLogin(access_token, data);
          }
        } else {
          setError("Failed to Authenticate!");
        }
      } else if (tokenresp.status === 201 && tokenresp?.auth == 2) {
        const { auth, access_token, data, id } = tokenresp;

        setError(
          "Your password has expired. You will now be redirected to reset it."
        );

        // Optional delay before redirecting (e.g., 1 second)
        setTimeout(() => {
          navigate(`/reset-password/${id}`);
        }, 2000);
      } else if (tokenresp.status === 202) {
        if (tokenresp.locked_status === "I") {
          setError("Your account is locked. Please contact administrator.");
        } else if (tokenresp.locked_status === "A") {
          setError(
            `Incorrect user credentials ${
              tokenresp.login_failed_count - 3
            } attempts remaining `
          );
        }
      } else if (tokenresp.status === 400 && typeof tokenresp.data === "string") {
        // e.g. "Invalid Practice ID" / "Please Provide Practice ID"
        setError(tokenresp.data);
      } else {
        setError("Failed!");
        showToast();
      }
    } catch (error) {
      console.error("An error occurred during authentication:", error);
      setError("Incorrect username or password. Try again.");
    }
  };

  // Modal Toggle Function
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    setModalError(""); // Reset modal error when toggling
  };

  // Validate Modal Fields
  const validateModalFields = () => {
    if (!email || !subject || !severity || !details || !name) {
      setModalError("All fields are required.");
      return false;
    }
    // Simple email validation
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    if (!emailPattern.test(email)) {
      setModalError("Please enter a valid email.");
      return false;
    }
    return true;
  };

  // Submit Modal Form
  const handleSubmitModal = async () => {
    if (validateModalFields()) {
      try {
        const response = await api.post(
          `/v1/${practiceCode.trim() || window.getPracticeId()}/support/create-ticket`,
          {
            name: name,
            priority: severity,
            email: email,
            status: 0,
            subject: subject,
            message: details,
            userid: 0,
          }
        );
        if (response.status == 200) {
          setModalError("Ticket Submitted Successfully!");

          setTimeout(() => {
            setName("");
            setSeverity("");
            setSubject("");
            setDetails("");
            setEmail("");
            setIsModalOpen(false);
          }, 1500);
        } else {
          setModalError(response.message);
        }
      } catch (error) {
        console.log(error);
      } finally {
      }
      // setIsModalOpen(false); // Close modal after submit
    }
  };

  const handleToastAction = () => {
    alert("Toast action button clicked!");
    navigate("/reset-password");
  };

  // Example Toast Trigger
  const showToast = () => {
    setToastMessage("Something went wrong");
    setToastSeverity("info");
    setOpenToast(true);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center items-center px-4 relative">
      {/* Logo Section */}
      <div className="flex items-center justify-center mb-8 w-full max-w-md">
        {/* Left logo */}
        <img
          src={BrianyCareLogo}
          alt="C-HIM Logo"
          className="w-10 h-10 object-contain mr-2"
        />

        {/* Text and divider */}
        <div className="flex items-center space-x-2 mr-1">
          <span className="text-xl font-bold text-[#1e90ff] leading-none">
            C-HIM
          </span>
          <span className="text-gray-400 text-sm">|</span>
        </div>

        {/* Right logo (fixed height alignment) */}
        <img
          src={BrainyMedLogo}
          alt="BrainyMed Logo"
          className="h-8 object-contain mt-[2px]" // mt balances vertical offset
        />
      </div>

      {/* Welcome Section */}
      <div className="w-full max-w-md text-left">
        <h2 className="text-2xl font-semibold mb-1 text-gray-900">
          Welcome back
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          Please enter your credentials to continue
        </p>

        {/* Practice code — runtime tenancy: one build serves every practice */}
        <label
          htmlFor="practicecode"
          className="block text-sm font-medium text-gray-700"
        >
          Practice Code
        </label>
        <input
          id="practicecode"
          type="text"
          inputMode="numeric"
          placeholder="Enter your practice code"
          value={practiceCode}
          onChange={(e) => setPracticeCode(e.target.value)}
          className="border border-gray-300 rounded-md p-2 text-sm w-full h-12 focus:outline-none focus:ring-2 focus:ring-[#1e90ff] mt-1 mb-4"
        />

        {/* Username */}
        <label
          htmlFor="username"
          className="block text-sm font-medium text-gray-700"
        >
          Username
        </label>
        <input
          id="username"
          type="text"
          placeholder="Enter your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="border border-gray-300 rounded-md p-2 text-sm w-full h-12 focus:outline-none focus:ring-2 focus:ring-[#1e90ff] mt-1"
        />

        {/* Password */}
        <label
          htmlFor="password"
          className="block text-sm font-medium text-gray-700 mt-4"
        >
          Password
        </label>
        <div className="relative mt-1">
          <input
            id="password"
            type={passwordVisible ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border border-gray-300 rounded-md p-2 text-sm w-full h-12 focus:outline-none focus:ring-2 focus:ring-[#1e90ff] mt-1"
          />
          <FontAwesomeIcon
            icon={passwordVisible ? faEyeSlash : faEye}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
            onClick={() => setPasswordVisible(!passwordVisible)}
          />
        </div>

        {error && <div className="text-red-500 text-sm mt-2">{error}</div>}

        {/* Forgot Password */}
        <div className="flex justify-between font-medium mt-2">
          <button
            onClick={toggleModal}
            className="text-sm text-[#1e90ff] hover:underline"
          >
            Support
          </button>
          <button
            onClick={() => navigate("/forgotpassword/?flow=forgot")}
            className="text-sm text-[#1e90ff] hover:underline"
          >
            Forgot Password?
          </button>
        </div>

        {/* Login Button */}
        <button
          onClick={handleLogin}
          className="bg-[#1e90ff] hover:bg-[#187bcd] text-white font-semibold h-12 p-2 rounded-md w-full mt-4 transition duration-200"
        >
          Log In
        </button>

        {/* Legal Text */}
        <p className="text-xs text-gray-500 mt-6 leading-relaxed">
          Your access and use of applications with this account are subject to
          the Terms of Use and Privacy Policy applicable to and found within
          those applications.
        </p>

        <hr className="border-t border-gray-300 mt-8 w-full" />
      </div>

      {/* Footer */}
      <div className="text-xs text-gray-500 mt-12 text-center">
        © {new Date().getFullYear()} BrainyMed, Inc
      </div>

      {/* Support Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl p-6 w-[400px] shadow-lg">
            <div className="flex justify-between items-center border-b pb-2">
              <div className="text-lg font-semibold text-gray-800">Support</div>
              <FontAwesomeIcon
                icon={faTimes}
                className="text-red-500 cursor-pointer"
                onClick={() => {
                  setName("");
                  setEmail("");
                  setSubject("");
                  setSeverity("");
                  setDetails("");
                  toggleModal();
                }}
              />
            </div>

            <div className="mt-4 text-start space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm"
                  placeholder="Enter subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Severity
                </label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm"
                >
                  <option value="">Select severity</option>
                  <option value="1">High</option>
                  <option value="2">Medium</option>
                  <option value="3">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Details
                </label>
                <textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows="4"
                  className="w-full border border-gray-300 rounded-md p-2 text-sm"
                  placeholder="Provide more details..."
                ></textarea>
              </div>

              {modalError && (
                <div
                  className={`text-sm mt-1 ${
                    modalError.includes("Successfully")
                      ? "text-green-600"
                      : "text-red-500"
                  }`}
                >
                  {modalError}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleSubmitModal}
                  className="bg-[#1e90ff] text-white px-4 py-2 rounded-md text-sm"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Message */}
      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={() => setOpenToast(false)}
      />
    </div>
  );
};
